import React from 'react';
import { useAuthenticatedImage, ImageSize } from '@/hooks/useAuthenticatedImage';

interface AuthenticatedImageProps {
  imageUrl: string; // URL like "/api/enrollment/serve-image?class_id=...&filename=..."
  alt?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  onError?: (error: string) => void;
  /**
   * Image size for thumbnailing:
   * - 'thumb': 150x150 (for small thumbnails in lists)
   * - 'small': 300x300 (small preview)
   * - 'medium': 600x600 (medium preview, default)
   * - 'large': 1200x1200 (large preview)
   * - 'full': original size
   */
  size?: ImageSize;
}

// Helper function to parse class_id and filename from imageUrl
const parseImageUrl = (imageUrl: string): { classId: string; filename: string } | null => {
  try {
    const url = new URL(imageUrl, window.location.origin);
    const classId = url.searchParams.get('class_id');
    const filename = url.searchParams.get('filename');

    if (!classId || !filename) {
      return null;
    }

    return { classId, filename };
  } catch (error) {
    console.error('Failed to parse image URL:', imageUrl, error);
    return null;
  }
};

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  imageUrl,
  alt = '',
  className = '',
  onClick,
  onError,
  size = 'medium'
}) => {
  const parsedUrl = React.useMemo(() => parseImageUrl(imageUrl), [imageUrl]);

  const { blobUrl, loading, error } = useAuthenticatedImage(
    parsedUrl?.classId || '',
    parsedUrl?.filename || '',
    size
  );

  React.useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Handle URL parsing failure
  if (!parsedUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-500 text-sm ${className}`}>
        Invalid URL
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-500 text-sm ${className}`}>
        Failed to load
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      onClick={onClick}
    />
  );
};

export default AuthenticatedImage;